import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useHolisticPlanIssues } from '../../hooks/data/useHolisticPlanIssues'
import type { HolisticPlanIssue } from '../../types/apiModels'
import { PROGRAM_IDS } from '../../utils/constants'
import type { Notice } from '../Notices/types'

type UseHolisticPlanIssuesNoticesParams = {
  farm: { id: string; programId?: number }
  year: string
  enabled: boolean
}

const NO_NOTICES: Notice[] = []

const ERROR_NOTICE: Notice = {
  id: 'holistic-plan-issues-error',
  severity: 'error',
  summary:
    'No se pudieron verificar las fechas de los planes holísticos. Intente nuevamente más tarde.',
}

const IssueSection = ({ title, issues }: { title: string; issues: HolisticPlanIssue[] }) => {
  if (!issues.length) return null

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
        {title}
      </Typography>
      {issues.map(issue => (
        <Typography key={`${issue.earlierPlan.id}-${issue.laterPlan.id}`} variant="body2">
          {issue.message}
        </Typography>
      ))}
    </Box>
  )
}

export const useHolisticPlanIssuesNotices = ({
  farm,
  year,
  enabled,
}: UseHolisticPlanIssuesNoticesParams): Notice[] => {
  const queryEnabled = enabled && farm.programId === PROGRAM_IDS.SARA && !!farm.id && !!year
  const { data, isError, isLoading } = useHolisticPlanIssues({
    farmId: farm.id,
    year,
    enabled: queryEnabled,
  })

  return useMemo(() => {
    if (!queryEnabled || isLoading) return NO_NOTICES
    if (isError) return [ERROR_NOTICE]
    if (!data) return NO_NOTICES

    const count = data.sameYear.length + data.crossYear.length
    if (!count) return NO_NOTICES

    return [
      {
        id: 'holistic-plan-issues',
        severity: 'warning',
        title: 'Fechas de planes holísticos',
        summary: `${count} ${count === 1 ? 'caso' : 'casos'} a corregir. Bloquean el envío de la declaración anual a revisión.`,
        detailLabel: 'Casos a corregir',
        detail: (
          <>
            <Typography variant="body2">
              Los registros se pueden guardar, pero el Técnico de Campo no podrá enviar la
              declaración anual a revisión hasta corregir estas fechas. Un plan puede comenzar el
              mismo día en que termina el anterior o el día siguiente.
            </Typography>
            <IssueSection issues={data.sameYear} title={`En ${year}`} />
            <IssueSection issues={data.crossYear} title={`Entre ${Number(year) - 1} y ${year}`} />
          </>
        ),
      },
    ]
  }, [queryEnabled, isLoading, isError, data, year])
}
